/**
 * "Complete the sofa set" upsell — parity with legacy M&H Living block-complete-the-sofa-set.
 * Keeps hidden inputs in sync whenever variant or checkbox state changes so Horizon's product-form
 * capture-phase handler reads correct FormData (same cart fields as Living: items[][id], items[][quantity]).
 */

/** @type {Set<HTMLElement>} */
const completeTheSofaSetRoots = new Set();

const CTS_DRAG_THRESHOLD = 6;

/**
 * Horizontal strip: Shift + mouse wheel scrolls sideways (native behavior varies by OS),
 * plus mouse drag-to-scroll (same pattern as the Google reviews / customer gallery
 * sliders in newcustom.js).
 * @param {HTMLElement | null} el
 */
function wireCtsSliderShiftWheel(el) {
  if (!(el instanceof HTMLElement)) return;
  el.addEventListener(
    'wheel',
    (e) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      if (!e.shiftKey) return;
      const dy = e.deltaY;
      const dx = e.deltaX;
      const delta = Math.abs(dy) >= Math.abs(dx) ? dy : dx;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    },
    { passive: false }
  );

  let isDown = false;
  let startX = 0;
  let startScroll = 0;
  let dragMoved = false;

  el.addEventListener('mousedown', (e) => {
    isDown = true;
    dragMoved = false;
    el.classList.add('is-dragging');
    startX = e.pageX - el.offsetLeft;
    startScroll = el.scrollLeft;
  });
  el.addEventListener('mouseleave', () => {
    isDown = false;
    el.classList.remove('is-dragging');
  });
  el.addEventListener('mouseup', () => {
    isDown = false;
    el.classList.remove('is-dragging');
  });
  el.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const walk = e.pageX - el.offsetLeft - startX;
    if (Math.abs(walk) > CTS_DRAG_THRESHOLD) dragMoved = true;
    el.scrollLeft = startScroll - walk;
  });

  let tStartX = 0;
  let tStartY = 0;
  el.addEventListener(
    'touchstart',
    (e) => {
      dragMoved = false;
      tStartX = e.touches[0].pageX;
      tStartY = e.touches[0].pageY;
    },
    { passive: true }
  );
  el.addEventListener(
    'touchmove',
    (e) => {
      const t = e.touches[0];
      if (Math.abs(t.pageX - tStartX) > CTS_DRAG_THRESHOLD || Math.abs(t.pageY - tStartY) > CTS_DRAG_THRESHOLD) {
        dragMoved = true;
      }
    },
    { passive: true }
  );

  // Swallow the click that immediately follows a drag/swipe (e.g. on the
  // checkbox overlay) so dragging never accidentally toggles a card.
  el.addEventListener(
    'click',
    (e) => {
      if (dragMoved) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    true
  );
}

document.addEventListener('variant:update', () => {
  completeTheSofaSetRoots.forEach((root) => {
    if (!document.body.contains(root)) {
      completeTheSofaSetRoots.delete(root);
      return;
    }
    const fn = /** @type {{ __ctsSync?: () => void }} */ (root).__ctsSync;
    fn?.();
  });
});

/**
 * @param {HTMLElement} root
 */
function initCompleteTheSofaSet(root) {
  if (root.dataset.ctsInitialized === 'true') return;
  root.dataset.ctsInitialized = 'true';

  const sectionId = root.dataset.sectionId;
  const blockId = root.dataset.blockId;
  const handlesEl = root.querySelector('[data-cts-handles]');

  if (!sectionId || !blockId || !handlesEl) return;

  /** @type {string[]} */
  let handles = [];
  try {
    handles = JSON.parse(handlesEl.textContent || '[]');
  } catch {
    return;
  }
  if (!Array.isArray(handles) || handles.length === 0) return;

  const currencyCode = root.dataset.currencyCode || 'USD';

  /** @type {HTMLFormElement | null} */
  const form = document.querySelector(
    `product-form-component[data-section-id="${CSS.escape(sectionId)}"] form`
  );
  if (!form) return;

  const wrapperDomId = 'cts-upsell-hidden-wrapper-' + blockId;
  let hiddenWrapper = document.getElementById(wrapperDomId);
  if (!hiddenWrapper) {
    hiddenWrapper = document.createElement('div');
    hiddenWrapper.id = wrapperDomId;
    hiddenWrapper.hidden = true;
    hiddenWrapper.setAttribute('aria-hidden', 'true');
    form.appendChild(hiddenWrapper);
  }

  const slider = root.querySelector('[data-slider]');
  wireCtsSliderShiftWheel(slider instanceof HTMLElement ? slider : null);

  /** @type {Record<string, { variants: { id: number; title: string; name?: string }[] }>} */
  const productData = {};

  /**
   * @param {number} cents
   */
  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currencyCode,
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  function getCurrentVariantTitle() {
    const sectionEl = document.querySelector('#shopify-section-' + CSS.escape(sectionId));
    const pickerScript = sectionEl?.querySelector('variant-picker script[type="application/json"]');
    if (pickerScript?.textContent) {
      try {
        const v = JSON.parse(pickerScript.textContent);
        if (v?.title) return /** @type {string} */ (v.title);
      } catch {
        /* ignore */
      }
    }
    return root.dataset.defaultVariantTitle || null;
  }

  function syncHiddenInputs() {
    if (!(hiddenWrapper instanceof HTMLDivElement)) return;
    hiddenWrapper.innerHTML = '';
    const variantTitle = getCurrentVariantTitle();

    root.querySelectorAll('[data-related-checkbox]:checked').forEach((cb) => {
      if (!(cb instanceof HTMLInputElement)) return;
      const handle = cb.dataset.productHandle;
      if (!handle) return;
      const info = productData[handle];
      if (!info || !variantTitle) return;

      let variantToUse = info.variants.find((v) => v.title === variantTitle || v.name === variantTitle);
      if (!variantToUse) variantToUse = info.variants[0];
      if (!variantToUse) return;

      const inputId = document.createElement('input');
      inputId.type = 'hidden';
      inputId.name = 'items[][id]';
      inputId.value = String(variantToUse.id);

      const inputQty = document.createElement('input');
      inputQty.type = 'hidden';
      inputQty.name = 'items[][quantity]';
      inputQty.value = '1';

      hiddenWrapper.appendChild(inputId);
      hiddenWrapper.appendChild(inputQty);
    });
  }

  /** @type {{ __ctsSync?: () => void }} */ (root).__ctsSync = syncHiddenInputs;
  completeTheSofaSetRoots.add(root);

  root.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.matches('[data-related-checkbox]')) {
      syncHiddenInputs();
    }
  });

  handles.forEach((handle) => {
    fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then((res) => res.json())
      .then((product) => {
        productData[product.handle] = {
          variants: product.variants.map(
            (/** @type {{ id: number; title: string; name?: string }} */ variant) => ({
              id: variant.id,
              title: variant.title,
              name: variant.name || variant.title,
            })
          ),
        };

        const price = formatMoney(product.price);
        const compare =
          product.compare_at_price && product.compare_at_price > product.price
            ? formatMoney(product.compare_at_price)
            : '';

        const imgSrc =
          typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image?.src || '';

        const card = document.createElement('div');
        card.className = 'complete-the-sofa-set__card';
        card.dataset.productHandle = product.handle;

        const mediaWrap = document.createElement('div');
        mediaWrap.className = 'complete-the-sofa-set__media-wrap';

        const mediaLink = document.createElement('a');
        mediaLink.className = 'complete-the-sofa-set__media-link';
        mediaLink.href = '/products/' + product.handle;

        const img = document.createElement('img');
        img.className = 'complete-the-sofa-set__image';
        img.src = imgSrc;
        img.alt = product.title || '';
        img.width = 280;
        img.height = 200;
        img.loading = 'lazy';

        mediaLink.appendChild(img);

        const label = document.createElement('label');
        label.className = 'complete-the-sofa-set__checkbox-label complete-the-sofa-set__checkbox-label--overlay';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'complete-the-sofa-set__checkbox';
        checkbox.dataset.relatedCheckbox = '';
        checkbox.dataset.productHandle = product.handle;
        checkbox.disabled = false;

        const span = document.createElement('span');
        span.className = 'complete-the-sofa-set__checkbox-text';
        span.textContent = root.dataset.checkboxLabel || '';

        label.appendChild(checkbox);
        label.appendChild(span);

        mediaWrap.appendChild(mediaLink);
        mediaWrap.appendChild(label);

        const body = document.createElement('div');
        body.className = 'complete-the-sofa-set__card-body';

        const titleRow = document.createElement('div');
        titleRow.className = 'complete-the-sofa-set__card-title-row';

        const titleLink = document.createElement('a');
        titleLink.className = 'complete-the-sofa-set__title-link link';
        titleLink.href = '/products/' + product.handle;
        titleLink.textContent = product.title || '';

        const priceWrap = document.createElement('div');
        priceWrap.className = 'complete-the-sofa-set__price';

        const saleSpan = document.createElement('span');
        saleSpan.className = 'complete-the-sofa-set__price-sale';
        saleSpan.textContent = price;

        priceWrap.appendChild(saleSpan);

        if (compare) {
          const cmp = document.createElement('s');
          cmp.className = 'complete-the-sofa-set__price-compare';
          cmp.textContent = compare;
          priceWrap.appendChild(cmp);
        }

        titleRow.appendChild(titleLink);
        titleRow.appendChild(priceWrap);

        body.appendChild(titleRow);

        card.appendChild(mediaWrap);
        card.appendChild(body);

        slider?.appendChild(card);

        syncHiddenInputs();
      })
      .catch(() => {
        /* ignore failed fetches */
      });
  });

  syncHiddenInputs();
}

function bootCompleteTheSofaSet() {
  document.querySelectorAll('[data-complete-the-sofa-set-root]').forEach((el) => {
    if (el instanceof HTMLElement) initCompleteTheSofaSet(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootCompleteTheSofaSet);
} else {
  bootCompleteTheSofaSet();
}
